import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import * as CryptoJS from 'crypto-js';
import { ToastrService } from 'ngx-toastr';
import { JWTTokenServiceService } from 'src/app/public/services/token.service';
import { showSubscriptionUsageLimitToastIfApplicable } from 'src/app/utils/subscription-api-error.util';
import { isPlainCustomer360ApiUrl } from 'src/app/utils/customer-360-capture-api.util';
import {
  isMailboxLinkRequiredResponse,
  toMailboxLinkError,
} from 'src/app/utils/mail-hub-response.util';

const SECRET_KEY = '12345678dsadsadas90abcdef1234567';

/** Part-d handlers not wrapped in secureAllHandlers expect plain JSON (no `{ payload }` cipher). */
function shouldSendPlainBody(url: string): boolean {
  if (typeof url !== 'string') return false;
  if (url.includes('getCodeForGoogleToken')) return true;
  // Public quote accept/reject Lambdas (no encrypted body)
  if (/\/acceptQuote\/[^/]+\b/.test(url) || /\/rejectQuote\/[^/]+\b/.test(url))
    return true;
  // Public Connect live-chat endpoints — called by the embedded chat widget (no auth, no encryption)
  const PLAIN_CHAT_ENDPOINTS = [
    'createWebChatLead',
    'createLiveChatLead',
    'startWebChat',
    'startConnectChat',
    'sendWebChatMessage',
    'sendConnectChatMessage',
    'getWebChatMessages',
    'endWebChat',
    'endConnectChat',
    'uploadWebChatAttachment',
    'uploadConnectChatAttachment',
    'getWebChatAttachmentUrl',
    'getConnectChatAttachmentUrl',
  ];
  if (PLAIN_CHAT_ENDPOINTS.some((ep) => url.includes(ep))) return true;
  if (isPlainCustomer360ApiUrl(url)) return true;
  const PLAIN_MEETING_BOOKING_ENDPOINTS = [
    'getandBookMeetingSlot',
  ];
  if (PLAIN_MEETING_BOOKING_ENDPOINTS.some((ep) => url.includes(ep))) return true;
  return false;
}

/** External bot endpoint — x-api-key header (no JWT / encryption). */
function isApiKeyOnlyMeetingUrl(url: string): boolean {
  if (typeof url !== 'string') return false;
  return url.includes('getandBookMeetingSlot');
}

/**
 * After AES decrypt, plaintext is JSON.parse'd once. If the backend encrypted a
 * stringified Lambda body (legacy part-c), plaintext can be a JSON *string* — parse again.
 * Objects/arrays from a single parse are returned as-is (no double-parse).
 */
function parseDecryptedApiBody(decrypted: any, _requestUrl: string): any {
  if (typeof decrypted === 'string') {
    try {
      return JSON.parse(decrypted);
    } catch {
      return decrypted;
    }
  }
  return decrypted;
}

@Injectable()
export class EncryptionInterceptor implements HttpInterceptor {
  constructor(
    private toastService: ToastrService,
    private tokenService: JWTTokenServiceService,
  ) {}

  encrypt(data: any): string {
    return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
  }

  decrypt(data: any): any {
    try {
      const bytes = CryptoJS.AES.decrypt(data, SECRET_KEY);

      const decryptedData = bytes.toString(CryptoJS.enc.Utf8);

      if (!decryptedData) {
        return data;
      }

      try {
        return JSON.parse(decryptedData);
      } catch {
        // plain string response
        return decryptedData;
      }
    } catch (e) {
      console.error('Decrypt Error:', e);

      return data;
    }
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (req.url && req.url.includes('sdr-ai-voice-bot')) {
      return next.handle(req);
    }
    let modifiedReq = req;
    let token = this.tokenService.getToken();

    if (!token || token == null || token == undefined || token == 'undefined') {
      token = localStorage.getItem('getpublicToken') || '';
    }

    // 🔐 Encrypt JSON bodies unless FormData or endpoint expects plain JSON (unsecured Lambda).
    if (req.body instanceof FormData) {
      modifiedReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${token}`),
      });
    } else if (isPlainCustomer360ApiUrl(req.url)) {
      modifiedReq = req;
    } else if (isApiKeyOnlyMeetingUrl(req.url)) {
      // Plain JSON; x-api-key from MeetingBookingService — no JWT, no encryption.
      modifiedReq = req;
    } else if (shouldSendPlainBody(req.url)) {
      modifiedReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${token}`),
      });
    } else {
      const encryptedBody = this.encrypt(req.body || {});
      modifiedReq = req.clone({
        body: { payload: encryptedBody },
        headers: req.headers.set('Authorization', `Bearer ${token}`),
      });
    }

    return next.handle(modifiedReq).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse && event.body?.response) {
          const decrypted = this.decrypt(event.body.response);
          const parsedBody = parseDecryptedApiBody(decrypted, req.url);
          console.log('Decrypted Response:', parsedBody);
          if (isMailboxLinkRequiredResponse(parsedBody)) {
            throw toMailboxLinkError(parsedBody);
          }
          return event.clone({
            body: parsedBody,
          });
        }
        if (
          event instanceof HttpResponse &&
          isMailboxLinkRequiredResponse(event.body)
        ) {
          throw toMailboxLinkError(event.body);
        }
        return event;
      }),
      catchError((error: unknown) => {
        if (isMailboxLinkRequiredResponse(error)) {
          return throwError(() => toMailboxLinkError(error));
        }
        if (!(error instanceof HttpErrorResponse)) {
          return throwError(() => error);
        }
        // Check if the error response is encrypted
        if (error.error?.response) {
          try {
            const decryptedError = this.decrypt(error.error.response);
            const parsedError = parseDecryptedApiBody(
              decryptedError,
              error.url || '',
            );

            // Reconstruct the error with decrypted message
            const modifiedError = new HttpErrorResponse({
              error: parsedError,
              headers: error.headers,
              status: error.status,
              statusText: error.statusText,
              url: error.url || undefined,
            });

            console.error('Decrypted Error:', parsedError);
            showSubscriptionUsageLimitToastIfApplicable(
              this.toastService,
              modifiedError,
            );
            return throwError(() => modifiedError);
          } catch (decryptError) {
            console.error('Failed to decrypt error response:', decryptError);
            // Show toast for decryption failure
            //this.toastService.error('Failed to process error response', 'Error');
          }
        }

        if (
          showSubscriptionUsageLimitToastIfApplicable(this.toastService, error)
        ) {
          return throwError(() => error);
        }

        // Handle non-encrypted errors - show toast message
        let errorMsg = '';
        if (error.error instanceof ErrorEvent) {
          errorMsg = `Error: ${error.error.message}`;
          this.toastService.error(errorMsg, 'Error');
        } else {
          // Extract meaningful error message from various structures
          let displayMsg = 'An error occurred';

          if (error.error) {
            if (typeof error.error === 'string') {
              displayMsg = error.error;
            } else if (error.error.message) {
              displayMsg = error.error.message;
            } else if (error.error.error) {
              displayMsg = error.error.error;
            }
          } else if (error.message) {
            displayMsg = error.message;
          }

          errorMsg = `Error Code: ${error.status}, Message: ${displayMsg}`;
          console.error(errorMsg);
          //this.toastService.error(displayMsg, `Error Code: ${error.status}`);
        }
        return throwError(() => error);
      }),
    );
  }
}
